import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Storage } from '@ionic/storage-angular';

import { LogUtil } from '../utils/log.util';
import { FUNCTIONARY_LOGIN_STORAGE_NAME, USER_TOKEN_STORAGE_NAME, CURRENT_USER, DOCUMENTS_STORAGE_NAME } from '../constants/storage.constant';
import { SalesDateFilter, SalesSyncFilter } from '../enums/sales.enum';
import { DateUtil } from '../utils/date.util';
import { Document } from '../../features/documents/interfaces/document.interface';

@Injectable({
	providedIn: 'root',
})
export class DBService {
	private _storage: Storage | null;


	constructor(
		private _angularStorage: Storage,
		private _router: Router
	) {
		this._storage = null;
		this.init();
	}

	/**
	 * Resolver
	 *
	 * @param route
	 * @param state
	 * @returns {Observable<any> | Promise<any> | any}
	 */
	async resolve(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): Promise<any> {
		// si existen datos locales, redirigimos a tabs.
		// esto se hace en el resolver a parte de en los guards, para manejar la pérdida de datos local.
		// si es que pudiera pasar.
		const a = await this.get(FUNCTIONARY_LOGIN_STORAGE_NAME);
		const b = await this.get(USER_TOKEN_STORAGE_NAME);

		if (a && b) {
			LogUtil.sendLog("redirigiendo");
			this._router.navigate(['tabs']);
		} else {
			LogUtil.sendLog("no redirigiendo");
		}

	}

	/**
	 * Inicializa la base de datos local
	 */
	async init(): Promise<void> {
		LogUtil.sendLog('init storage');
		// If using, define drivers here: await this.storage.defineDriver(/*...*/);
		const storage = await this._angularStorage.create();
		this._storage = storage;

	}

	/**
	 * Retorna el elemento guardado en la BD dado su key
	 *
	 * @param key
	 * @param addPrefix indica si agregamos a cada key el prefijo correspondiente al usuario actual. Por defecto es true. Deberia ser false al obtener el usuario actual.
	 * @returns el objeto buscado o null si no encuentra
	 */
	async get<T>(key: string, addPrefix: boolean = true): Promise<T> {
		if (addPrefix) {
			key = await this.getKeyWithPrefix(key);
		}

		return await this._storage?.get(key);
	}

	/**
	 * elimina un item de la BD local
	 * @param key
	 * @param addPrefix indica si agregamos a cada key el prefijo correspondiente al usuario actual. Por defecto es true
	 */
	async remove(key: string, addPrefix: boolean = true): Promise<void> {
		if (addPrefix) {
			key = await this.getKeyWithPrefix(key);
		}

		await this._storage?.remove(key);
	}

	/**
	 * Agrega un elemento único a la BD local con el key indicado.
	 * Se elimina previamente para asegurarse de que se agregue de manera única.
	 *
	 * @param key
	 * @param value
	 * @param addPrefix indica si agregamos a cada key el prefijo correspondiente al usuario actual. Por defecto es true. Deberia ser false al guardar el usuario actual.
	 */
	async setUnique(key: string, value: any, addPrefix: boolean = true): Promise<void> {
		await this.remove(key, addPrefix);
		this.set(key, value, addPrefix);
	}

	/**
	 * Agrega un elemento a la BD local con el key indicado.
	 *
	 * @param key
	 * @param value
	 * @param addPrefix indica si agregamos a cada key el prefijo correspondiente al usuario actual. Por defecto es true. Deberia ser false al guardar el usuario actual.
	 */
	async set(key: string, value: any, addPrefix: boolean = true): Promise<void> {
		if (addPrefix) {
			key = await this.getKeyWithPrefix(key);
		}

		await this._storage?.set(key, value);
	}

	/**
	 * limpia todo local storage y BD local.
	 */
	async clear(): Promise<void> {
		await this._storage?.clear();
	}



	/**
	 * Retorna la key con el nombre de usuario como prefijo.
	 * @param key
	 * @returns
	 */
	async getKeyWithPrefix(key: string): Promise<string> {
		const prefix = await this.get<string>(CURRENT_USER, false) || "";

		return `${prefix}-${key}`;
	}

	/**
	 * Retorna el listado de documentos creados.
	 *
	 * @param dateFiler
	 * @param syncFilter
	 * @returns
	 */
	async getDocuments(dateFiler: SalesDateFilter, syncFilter: SalesSyncFilter): Promise<Document[]> {
		// obtenemos todos los documentos locales.
		// buscamos todos los elementos guardados con un key que contenga "document-"
		// ya que sabemos que así los guardamos.
		const docs = await this.get<Document[]>(DOCUMENTS_STORAGE_NAME);
		let docsFilterDate: Document[];

		// aplicamos filtros según el dateFilter.
		switch (dateFiler) {
			case SalesDateFilter.Today:
				docsFilterDate = docs.filter(d => DateUtil.isToday(new Date(d.ownOpt.createdAt)));
				break;
			case SalesDateFilter.LastHour:
				docsFilterDate = docs.filter(d => DateUtil.inLastHour(new Date(d.ownOpt.createdAt)));
				break;
			case SalesDateFilter.ThisWeek:
				docsFilterDate = docs.filter(d => DateUtil.inLastWeek(new Date(d.ownOpt.createdAt)));
				break;
			default:
				docsFilterDate = docs;

		}

		// aplicamos filtro sobre estado de sincronizacion del documento
		switch (syncFilter) {
			case SalesSyncFilter.NotSync:
				return docsFilterDate.filter(d => !d.ownOpt.uploadedAt);
			case SalesSyncFilter.Sync:
				return docsFilterDate.filter(d => d.ownOpt.uploadedAt);
			default:
				// ambos estados
				return docsFilterDate;
		}

	}
}
